import { useCallback, useEffect, useMemo, useState } from 'react';
import { Add, DeleteOutline } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import uuid from 'react-uuid';
import {
  DailyNavReportColumnMapping,
  DailyNavReportMappingField,
  DailyNavReportTemplateUploadResponse,
  FirmDailyNavConfigVersion,
} from 'api';
import { firmDailyNavUrl } from 'common/config/urls';
import {
  buildDailyNavFirmConfigDiff,
  DAILY_NAV_APPLY_BUTTON_LABEL,
  DAILY_NAV_APPLY_MARKET_OR_WINDOWS_CHANGE_WARNING,
  DAILY_NAV_APPLY_STANDING_EXPLANATION,
  DAILY_NAV_DEFAULT_LONG_WINDOW_SESSIONS,
  DAILY_NAV_DEFAULT_REPORT_SEND_TIME,
  DAILY_NAV_DEFAULT_REPORTING_CURRENCY,
  DAILY_NAV_DEFAULT_SHORT_WINDOW_SESSIONS,
  DAILY_NAV_DEFAULT_TRADING_MARKET,
  DAILY_NAV_FIRM_DEFAULTS_COPY,
  DAILY_NAV_THRESHOLD_KEYS,
  DAILY_NAV_TRADING_MARKET_OPTIONS,
  DAILY_NAV_WINDOW_SESSIONS_FIELDS,
  dailyNavApplyDatesLabel,
  dailyNavApplyDisabledReason,
  dailyNavApplyRefusalMessage,
  dailyNavDateLabel,
  dailyNavMarketTimezone,
  dailyNavNextDayLabel,
  DailyNavReportingCurrency,
  DailyNavThresholdKey,
  DailyNavTradingMarket,
  DailyNavWindowSessionsKey,
  formatDailyNavReportSendTime,
  fractionToPercent,
  getDailyNavThresholdGroups,
  isShortWindowBeforeLongWindow,
  isValidReportSendTime,
  isValidThresholdPercent,
  isValidWindowSessions,
  parseDailyNavReportSendTime,
  percentToFraction,
  REPORT_SEND_TIME_MINUTE_STEP,
  selectDailyNavApplyDates,
  selectDailyNavGoverningVersions,
  snapDailyNavReportSendTime,
  tradingMarketLabel,
  WINDOW_SESSIONS_MAX,
  WINDOW_SESSIONS_MIN,
} from 'common/constants/dailyNav';
import { DAILY_NAV_SETTINGS_PAGE_TITLE, FIRM_SETTINGS } from 'common/constants/firms';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import DailyNavConfigDiff from 'components/DailyNavConfigDiff';
import { GridSkeleton } from 'components/Grid';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { buildFirmSettingsNavigationItems } from 'pages/Firms/firmSettingsNavigation';
import { useReadUserAccessLevel } from 'services/hooks';
import {
  useApplyDailyNavConfig,
  useGetDailyNavConfigApplyPreview,
  useGetFirmDailyNavConfig,
  useGetFirmDailyNavConfigHistory,
  useUpdateFirmDailyNavConfig,
} from 'services/hooks/dailyNav';
import { useUploadDailyNavReportTemplate } from 'services/hooks/firms/useDailyNavReport';
import DailyNavFtpConfig from './DailyNavFtpConfig';
import DailyNavTemplateMapper, { areDailyNavReportMappingsComplete } from './DailyNavTemplateMapper';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_THRESHOLDS = Object.fromEntries(DAILY_NAV_THRESHOLD_KEYS.map(key => [key, ''])) as Record<
  DailyNavThresholdKey,
  string
>;

type RecipientRow = { email: string; key: string };

const formatEffectivePeriod = (version: FirmDailyNavConfigVersion) => {
  const from = format(new Date(version.effective_from), 'MMM d, yyyy h:mm a');
  const to = version.effective_to ? format(new Date(version.effective_to), 'MMM d, yyyy h:mm a') : 'Present';
  return `${from} — ${to}`;
};

const DailyNavSettings = () => {
  const [{ firmInfo, user }] = useStore() as unknown as UseStoreValues;
  const { enqueueSnackbar } = useSnackbar();

  const firmId = firmInfo?.id ?? null;
  const firmSlug = firmInfo?.slug ?? '';

  const { isAdminOrStaffOrSuperuser } = useReadUserAccessLevel({ user, firmInfo });

  const { data: config, isLoading } = useGetFirmDailyNavConfig(firmId);
  const { history, loadMoreHistory, hasMoreHistory, isLoadingMoreHistory } = useGetFirmDailyNavConfigHistory(firmId);
  const { updateFirmDailyNavConfig, isPending } = useUpdateFirmDailyNavConfig();
  const { data: applyPreview, isLoading: isApplyPreviewLoading, refetch: refetchApplyPreview } =
    useGetDailyNavConfigApplyPreview(firmId);
  const { applyDailyNavConfig, isPending: isApplyPending } = useApplyDailyNavConfig();
  const { mutateAsync: uploadReportTemplate, isPending: isUploadingTemplate } = useUploadDailyNavReportTemplate();

  const [thresholds, setThresholds] = useState<Record<DailyNavThresholdKey, string>>(EMPTY_THRESHOLDS);
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [tradingMarket, setTradingMarket] = useState('');
  const [shortWindowSessions, setShortWindowSessions] = useState('');
  const [longWindowSessions, setLongWindowSessions] = useState('');
  const [reportSendTime, setReportSendTime] = useState(DAILY_NAV_DEFAULT_REPORT_SEND_TIME);
  const [ftpHost, setFtpHost] = useState('');
  const [ftpPort, setFtpPort] = useState('21');
  const [ftpPath, setFtpPath] = useState('');
  const [ftpUsername, setFtpUsername] = useState('');
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [reportTemplateId, setReportTemplateId] = useState<number | null>(null);
  const [reportTemplateFilename, setReportTemplateFilename] = useState('');
  const [reportColumnMappings, setReportColumnMappings] = useState<DailyNavReportColumnMapping[]>([]);
  const [reportMappingFields, setReportMappingFields] = useState<DailyNavReportMappingField[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  // Null = closed. An object, not an id-as-flag: the dialog opens after EVERY save,
  // including when there is nothing to apply. `fromSave` only picks the wording, since
  // the Version History button opens the same dialog without saving.
  const [applyDialog, setApplyDialog] = useState<{ versionId: number | null; fromSave: boolean } | null>(null);
  const applyVersionId = applyDialog?.versionId ?? null;

  // `dates` also carries the days that CAN'T be applied, so the applicable ones have to be
  // filtered out rather than read off the front -- see selectDailyNavApplyDates.
  const { hasDates, applicableDates, recordCount, marketOrWindowsChange, blockedDate } = useMemo(
    () => selectDailyNavApplyDates(applyPreview?.dates),
    [applyPreview?.dates]
  );
  // Which version today's NAV is actually running on vs. which the next day will use.
  const {
    anchoredVersionId,
    openVersionId,
    navDate: governingNavDate,
    nextNavDate,
    hasDivergence,
  } = useMemo(() => selectDailyNavGoverningVersions(applyPreview), [applyPreview]);

  const isApplyPreviewDateApplicable = applicableDates.length > 0;
  // Avoids flashing the empty-state hint before the preview has loaded.
  const hasNoOpenNavDay = !isApplyPreviewLoading && !hasDates;

  const windowSessionsState: Record<DailyNavWindowSessionsKey, string> = {
    short_window_sessions: shortWindowSessions,
    long_window_sessions: longWindowSessions,
  };
  const windowSessionsSetters: Record<DailyNavWindowSessionsKey, (value: string) => void> = {
    short_window_sessions: setShortWindowSessions,
    long_window_sessions: setLongWindowSessions,
  };

  // The firm has never saved a template: the API returns unpersisted model
  // defaults (no id) just so the page has something to render. Leave the
  // form blank rather than pre-filling those defaults, so the admin has to
  // deliberately enter every threshold before the first version can be saved.
  const hasSavedConfig = Boolean(config?.id);

  useEffect(() => {
    if (config) {
      setThresholds(
        hasSavedConfig
          ? (Object.fromEntries(DAILY_NAV_THRESHOLD_KEYS.map(key => [key, fractionToPercent(config[key])])) as Record<
              DailyNavThresholdKey,
              string
            >)
          : EMPTY_THRESHOLDS
      );
      const configRecipients =
        hasSavedConfig && Array.isArray(config.notification_recipients)
          ? (config.notification_recipients as string[])
          : [];
      setRecipients(configRecipients.map(email => ({ email, key: uuid() })));
      // Unlike the thresholds, the market and window session counts are seeded
      // when nothing is saved yet, matching their API-side defaults.
      setTradingMarket(
        hasSavedConfig ? (config.trading_calendar ?? '') : DAILY_NAV_DEFAULT_TRADING_MARKET
      );
      setShortWindowSessions(
        String(hasSavedConfig ? (config.short_window_sessions ?? '') : DAILY_NAV_DEFAULT_SHORT_WINDOW_SESSIONS)
      );
      setLongWindowSessions(
        String(hasSavedConfig ? (config.long_window_sessions ?? '') : DAILY_NAV_DEFAULT_LONG_WINDOW_SESSIONS)
      );
      setReportSendTime(
        hasSavedConfig
          ? formatDailyNavReportSendTime(config.report_send_time)
          : DAILY_NAV_DEFAULT_REPORT_SEND_TIME
      );
      setFtpHost(hasSavedConfig ? (config.ftp_host ?? '') : '');
      setFtpPort(String(hasSavedConfig ? (config.ftp_port ?? 21) : 21));
      setFtpPath(hasSavedConfig ? (config.ftp_path ?? '') : '');
      setFtpUsername(hasSavedConfig ? (config.ftp_username ?? '') : '');
      setTemplateFile(null);
      setReportTemplateId(config.report_template ?? null);
      setReportTemplateFilename(config.report_template_filename ?? '');
      setReportColumnMappings(config.report_column_map ?? []);
      setReportMappingFields(config.report_mapping_fields ?? []);
    }
  }, [config, hasSavedConfig]);

  const isWindowOrderValid = useMemo(
    () =>
      isValidWindowSessions(shortWindowSessions) &&
      isValidWindowSessions(longWindowSessions) &&
      isShortWindowBeforeLongWindow(shortWindowSessions, longWindowSessions),
    [shortWindowSessions, longWindowSessions]
  );

  // From what is typed, so editing a window relabels its thresholds at once.
  const liveThresholdGroups = useMemo(
    () => getDailyNavThresholdGroups(shortWindowSessions, longWindowSessions),
    [shortWindowSessions, longWindowSessions]
  );

  // Per field, so an out-of-range value flags only itself; the ordering rule
  // flags both when the bounds are fine but short >= long.
  const isShortWindowSessionsInvalid =
    !isValidWindowSessions(shortWindowSessions) ||
    (isValidWindowSessions(longWindowSessions) && !isWindowOrderValid);
  const isLongWindowSessionsInvalid =
    !isValidWindowSessions(longWindowSessions) ||
    (isValidWindowSessions(shortWindowSessions) && !isWindowOrderValid);

  const areReportMappingsComplete = useMemo(
    () => areDailyNavReportMappingsComplete(reportTemplateId, reportColumnMappings),
    [reportTemplateId, reportColumnMappings]
  );

  const isFormValid = useMemo(
    () =>
      DAILY_NAV_THRESHOLD_KEYS.every(key => isValidThresholdPercent(thresholds[key])) &&
      recipients.every(recipient => EMAIL_PATTERN.test(recipient.email.trim())) &&
      Boolean(tradingMarket) &&
      isWindowOrderValid &&
      isValidReportSendTime(reportSendTime) &&
      areReportMappingsComplete,
    [thresholds, recipients, tradingMarket, isWindowOrderValid, reportSendTime, areReportMappingsComplete]
  );

  const isDirty = useMemo(() => {
    const baselineThresholds = hasSavedConfig
      ? (Object.fromEntries(DAILY_NAV_THRESHOLD_KEYS.map(key => [key, fractionToPercent(config?.[key])])) as Record<
          DailyNavThresholdKey,
          string
        >)
      : EMPTY_THRESHOLDS;
    const thresholdsChanged = DAILY_NAV_THRESHOLD_KEYS.some(key => thresholds[key] !== baselineThresholds[key]);

    const baselineRecipients =
      hasSavedConfig && Array.isArray(config?.notification_recipients)
        ? (config?.notification_recipients as string[])
        : [];
    const currentRecipients = recipients.map(recipient => recipient.email.trim());
    const recipientsChanged =
      currentRecipients.length !== baselineRecipients.length ||
      currentRecipients.some((email, index) => email !== baselineRecipients[index]);

    const baselineMarket = hasSavedConfig
      ? (config?.trading_calendar ?? '')
      : DAILY_NAV_DEFAULT_TRADING_MARKET;
    const marketChanged = tradingMarket !== baselineMarket;


    const baselineShortWindowSessions = String(
      hasSavedConfig ? (config?.short_window_sessions ?? '') : DAILY_NAV_DEFAULT_SHORT_WINDOW_SESSIONS
    );
    const baselineLongWindowSessions = String(
      hasSavedConfig ? (config?.long_window_sessions ?? '') : DAILY_NAV_DEFAULT_LONG_WINDOW_SESSIONS
    );
    const windowSessionsChanged =
      shortWindowSessions !== baselineShortWindowSessions || longWindowSessions !== baselineLongWindowSessions;

    const baselineReportSendTime = hasSavedConfig
      ? formatDailyNavReportSendTime(config?.report_send_time)
      : DAILY_NAV_DEFAULT_REPORT_SEND_TIME;
    const reportSendTimeChanged = reportSendTime !== baselineReportSendTime;

    const reportTemplateChanged =
      reportTemplateId !== (config?.report_template ?? null) ||
      JSON.stringify(reportColumnMappings) !== JSON.stringify(config?.report_column_map ?? []);

    const ftpChanged =
      ftpHost !== (hasSavedConfig ? (config?.ftp_host ?? '') : '') ||
      ftpPort !== String(hasSavedConfig ? (config?.ftp_port ?? 21) : 21) ||
      ftpPath !== (hasSavedConfig ? (config?.ftp_path ?? '') : '') ||
      ftpUsername !== (hasSavedConfig ? (config?.ftp_username ?? '') : '');

    return (
      thresholdsChanged ||
      recipientsChanged ||
      marketChanged ||
      windowSessionsChanged ||
      reportSendTimeChanged ||
      reportTemplateChanged ||
      ftpChanged
    );
  }, [
    config,
    hasSavedConfig,
    thresholds,
    recipients,
    tradingMarket,
    shortWindowSessions,
    longWindowSessions,
    reportSendTime,
    ftpHost,
    ftpPort,
    ftpPath,
    ftpUsername,
    reportTemplateId,
    reportColumnMappings,
  ]);

  const canSave = isFormValid && isDirty && !isUploadingTemplate;

  // An applicable date is not enough: applicableDates filters on blocked_reason only,
  // so in the steady state every record is already current and applying moves nothing.
  const hasNothingToApply = isApplyPreviewDateApplicable && recordCount === 0;
  const canApplyNow = isApplyPreviewDateApplicable && recordCount > 0;

  const isApplyDisabled = !firmId || isApplyPending || isDirty || !canApplyNow;
  const applyDisabledReason = dailyNavApplyDisabledReason({
    isDisabled: isApplyDisabled,
    isDirty,
    hasNoOpenNavDay,
    hasNothingToApply,
    blockedDate,
  });

  // A stored value could predate the curated list; append it rather than
  // rendering an empty picker.
  const marketOptions = useMemo(() => {
    if (!tradingMarket || DAILY_NAV_TRADING_MARKET_OPTIONS.some(option => option.value === tradingMarket)) {
      return DAILY_NAV_TRADING_MARKET_OPTIONS;
    }
    return [...DAILY_NAV_TRADING_MARKET_OPTIONS, { value: tradingMarket, label: tradingMarket }];
  }, [tradingMarket]);

  const applyUploadedTemplate = useCallback((uploaded: DailyNavReportTemplateUploadResponse) => {
    setReportTemplateId(uploaded.id);
    setReportTemplateFilename(uploaded.filename);
    setReportMappingFields(uploaded.mapping_fields);
    setReportColumnMappings(
      uploaded.columns.map((columnName, columnIndex) => ({
        column_index: columnIndex,
        column_name: columnName,
        kind: DailyNavReportColumnMapping.kind.FIELD,
      }))
    );
  }, []);

  const handleTemplateFileChange = useCallback(
    async (file: File | null) => {
      setTemplateFile(file);
      if (!file || !firmId) {
        return;
      }
      try {
        const uploaded = await uploadReportTemplate({ firmId, file });
        applyUploadedTemplate(uploaded);
      } catch {
        setTemplateFile(null);
      }
    },
    [firmId, uploadReportTemplate, applyUploadedTemplate]
  );

  const saveDailyNavSettings = useCallback(async () => {
    if (!firmId) {
      return;
    }
    // Before canSave, which already covers it but gives no feedback.
    if (!isWindowOrderValid) {
      enqueueSnackbar('The short window must be fewer trading sessions than the long window', {
        variant: 'error',
      });
      return;
    }
    if (!canSave) {
      return;
    }
    const updatedConfig = await updateFirmDailyNavConfig(firmId, {
      cap_iq_short_window_delta: percentToFraction(thresholds.cap_iq_short_window_delta),
      cap_iq_long_window_delta: percentToFraction(thresholds.cap_iq_long_window_delta),
      cap_iq_since_last_valuation_delta: percentToFraction(thresholds.cap_iq_since_last_valuation_delta),
      secondary_short_window_delta: percentToFraction(thresholds.secondary_short_window_delta),
      secondary_long_window_delta: percentToFraction(thresholds.secondary_long_window_delta),
      secondary_since_last_valuation_delta: percentToFraction(thresholds.secondary_since_last_valuation_delta),
      secondary_change_from_mark_delta: percentToFraction(thresholds.secondary_change_from_mark_delta),
      notification_recipients: recipients.map(recipient => recipient.email.trim()),
      // The generated read and write models declare separate identical enums,
      // and TS string enums are nominal, so the cast is required.
      trading_calendar: tradingMarket as DailyNavTradingMarket,
      // Not user-editable: the API requires a currency, so a saved value is
      // round-tripped and anything else defaults to USD. Deliberately read from
      // `config` rather than form state -- a hidden field in the validity check
      // would block saving with nothing on screen to explain why.
      //
      // `||`, NOT `??`: a firm with no saved template gets the unsaved-instance
      // placeholder, whose CharField serializes as "". ?? passes "" straight to
      // a required ChoiceField, so the very first save 400s on a field that
      // isn't on screen to explain itself.
      reporting_currency: (config?.reporting_currency ||
        DAILY_NAV_DEFAULT_REPORTING_CURRENCY) as DailyNavReportingCurrency,
      short_window_sessions: Number(shortWindowSessions),
      long_window_sessions: Number(longWindowSessions),
      report_send_time: reportSendTime,
      ...(reportTemplateId !== null
        ? {
          report_template: reportTemplateId,
          report_column_map: reportColumnMappings,
        }
        : {}),
      ftp_host: ftpHost || null,
      ftp_port: Number(ftpPort) || 21,
      ftp_path: ftpPath || null,
      ftp_username: ftpUsername || null,
    });
    if (!updatedConfig) {
      return;
    }
    enqueueSnackbar('Daily NAV settings successfully saved', { variant: 'success' });

    // Offer to apply to the day in progress rather than making the operator find the
    // new version in Version History and apply it from there.
    //
    // Read the preview from refetch's OWN return value, never from `applyPreview` in
    // this closure: that is the pre-save preview, whose counts were computed against
    // the version this save just superseded. Its record_count is typically 0 (every
    // record already matched the then-open version), which would silently skip the
    // prompt in exactly the case it exists for. The save already invalidates this
    // query; refetching is what gives this callback the fresh value to decide on.
    // Opened unconditionally: what changed and when it takes effect are worth stating
    // on every save. Whether the APPLY is offered is decided inside, from the preview.
    // Refetched first because the preview in this closure predates the save -- its
    // record_count is typically 0, which would wrongly suppress the offer.
    await refetchApplyPreview();
    setApplyDialog({ versionId: updatedConfig.id ?? null, fromSave: true });
  }, [
    firmId,
    canSave,
    isWindowOrderValid,
    thresholds,
    recipients,
    tradingMarket,
    config,
    shortWindowSessions,
    longWindowSessions,
    reportSendTime,
    ftpHost,
    ftpPort,
    ftpPath,
    ftpUsername,
    reportTemplateId,
    reportColumnMappings,
    updateFirmDailyNavConfig,
    refetchApplyPreview,
    enqueueSnackbar,
  ]);

  const handleConfirmApplyToOpenNavDay = useCallback(async () => {
    if (!firmId) {
      return;
    }
    if (applyVersionId === null) {
      return;
    }
    setApplyDialog(null);
    const response = await applyDailyNavConfig(firmId, applyVersionId);
    if (!response) {
      return;
    }
    if (!response.reanchored_count) {
      // A refusal and an idempotent no-op both come back 200 with 0 moved; only the
      // response's own blocked_reason separates them.
      const refusal = dailyNavApplyRefusalMessage(response.dates);
      enqueueSnackbar(refusal || 'Nothing needed applying — records already reflect the open config', {
        variant: refusal ? 'warning' : 'info',
      });
      return;
    }
    enqueueSnackbar(
      `Re-pointed ${response.reanchored_count} record${response.reanchored_count === 1 ? '' : 's'} onto the open config`,
      { variant: 'success' }
    );
    // applyVersionId must stay in the deps: it is read here, so omitting it pins this
    // callback to the render that created it -- where the dialog was still closed and the
    // id null, making the confirm button a silent no-op.
  }, [firmId, applyVersionId, applyDailyNavConfig, enqueueSnackbar]);

  const secondaryNavigation = useMemo(
    () => ({
      title: FIRM_SETTINGS,
      items: buildFirmSettingsNavigationItems(firmSlug, { dailyNavEnabled: true }),
      actions: isAdminOrStaffOrSuperuser
        ? [
          {
            type: 'button' as const,
            key: 'firm-daily-nav-settings-save',
            label: 'Save',
            variant: 'positive' as const,
            onClick: saveDailyNavSettings,
            disabled: !firmId || !canSave || isPending || isUploadingTemplate,
          },
        ]
        : [],
    }),
    [firmSlug, isAdminOrStaffOrSuperuser, saveDailyNavSettings, firmId, canSave, isPending, isUploadingTemplate]
  );

  useSecondaryNavigationRegistration(secondaryNavigation);

  const selectedVersion = useMemo(
    () => history?.find(version => version.id === selectedVersionId) ?? null,
    [history, selectedVersionId]
  );

  // What applying would change, read out of the loaded history so it costs no request.
  // History is paginated: an anchored version off the first page renders empty.
  const divergenceDiff = useMemo(
    () =>
      buildDailyNavFirmConfigDiff(
        history?.find(version => version.id === anchoredVersionId),
        history?.find(version => version.id === openVersionId)
      ),
    [history, anchoredVersionId, openVersionId]
  );

  // divergenceDiff is empty when there is no open NAV day to diverge from -- exactly
  // when the dialog still opens. Fall back to what the save changed. History is ordered
  // -effective_from, so the entry AFTER a version is its predecessor.
  const dialogDiff = useMemo(() => {
    if (divergenceDiff.length) {
      return divergenceDiff;
    }
    const index = history?.findIndex(version => version.id === applyVersionId) ?? -1;
    return index < 0 ? [] : buildDailyNavFirmConfigDiff(history?.[index + 1], history?.[index]);
  }, [divergenceDiff, history, applyVersionId]);

  // Why an apply is not on offer, for the acknowledgement form of the dialog.
  const applyUnavailableReason = useMemo(
    () => dailyNavApplyRefusalMessage(applyPreview?.dates),
    [applyPreview?.dates]
  );

  // A decision when a day is open, an acknowledgement otherwise. Only the save path
  // may claim anything was saved.
  const applyDialogTitle = useMemo(() => {
    if (!canApplyNow) {
      return 'Daily NAV settings saved';
    }
    return applyDialog?.fromSave
      ? 'Settings saved — apply to the open NAV day?'
      : 'Apply these settings to the open NAV day?';
  }, [canApplyNow, applyDialog?.fromSave]);

  const handleAddRecipient = () => {
    setRecipients(prevState => [...prevState, { email: '', key: uuid() }]);
  };

  const handleRemoveRecipient = (index: number) => {
    setRecipients(prevState => prevState.filter((_, i) => i !== index));
  };

  const handleRecipientChange = (index: number, value: string) => {
    setRecipients(prevState =>
      prevState.map((recipient, i) => (i === index ? { ...recipient, email: value } : recipient))
    );
  };

  const handleThresholdChange = (key: DailyNavThresholdKey, value: string) => {
    setThresholds(prevState => ({ ...prevState, [key]: value }));
  };

  const handleWindowSessionsChange = (key: DailyNavWindowSessionsKey, value: string) => {
    windowSessionsSetters[key](value);
  };

  const windowSessionsErrors: Record<DailyNavWindowSessionsKey, boolean> = {
    short_window_sessions: isShortWindowSessionsInvalid,
    long_window_sessions: isLongWindowSessionsInvalid,
  };

  if (isLoading) {
    return <GridSkeleton />;
  }

  return (
    <Grid container spacing={4} sx={{ mb: 10, pt: 1, flexDirection: { xs: 'column', lg: 'row' } }}>
      <Grid size={{ xs: 12, lg: 7 }}>
        <Typography variant="h6" gutterBottom>
          {DAILY_NAV_SETTINGS_PAGE_TITLE}
        </Typography>
        {/* Pairs with the Daily NAV page's link back here. */}
        {firmSlug && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <Link component={RouterLink} to={firmDailyNavUrl(firmSlug)} data-testid="daily-nav-settings-grid-link">
              View Daily NAV
            </Link>
          </Typography>
        )}
        <Typography variant="h4" gutterBottom sx={{ mt: 3 }}>
          Default Alert Thresholds
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          {DAILY_NAV_FIRM_DEFAULTS_COPY}
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          The number of trading sessions each delta window looks back. The short window must be less than the long
          window; both must be between {WINDOW_SESSIONS_MIN} and {WINDOW_SESSIONS_MAX} trading sessions.
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {DAILY_NAV_WINDOW_SESSIONS_FIELDS.map(field => (
            <Grid key={field.key} size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                id={`daily-nav-window-sessions-${field.key}`}
                label={field.label}
                value={windowSessionsState[field.key]}
                onChange={e => handleWindowSessionsChange(field.key, e.target.value)}
                error={windowSessionsErrors[field.key]}
                disabled={!isAdminOrStaffOrSuperuser}
                slotProps={{
                  htmlInput: { min: WINDOW_SESSIONS_MIN, max: WINDOW_SESSIONS_MAX, step: 1 },
                }}
              />
            </Grid>
          ))}
        </Grid>
        {!isWindowOrderValid &&
          isValidWindowSessions(shortWindowSessions) &&
          isValidWindowSessions(longWindowSessions) && (
          <Typography variant="caption" color="error" sx={{ mb: 3, display: 'block' }}>
              The short window must be less than the long window.
          </Typography>
        )}
        {!hasSavedConfig && (
          <Alert severity="info" sx={{ mb: 3 }} data-testid="daily-nav-no-config-alert">
            This firm has not configured Daily NAV monitoring yet. Enter a value for every threshold below, then Save to
            activate monitoring.
          </Alert>
        )}
        {/* Says outright that today's NAV is not on the saved settings. */}
        {hasDivergence && (
          <Alert severity="warning" sx={{ mb: 3 }} data-testid="daily-nav-divergence-alert">
            <Typography variant="body2" sx={{ mb: divergenceDiff.length ? 1 : 0 }}>
              The NAV day in progress ({dailyNavDateLabel(governingNavDate)}) is still being evaluated under an earlier
              version of these settings. {dailyNavNextDayLabel(nextNavDate)}. Use Apply in Version History to bring the
              day in progress onto what you see below.
            </Typography>
            <DailyNavConfigDiff rows={divergenceDiff} testId="daily-nav-divergence-diff" />
          </Alert>
        )}
        {hasSavedConfig && !isDirty && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 3, display: 'block' }}
            data-testid="daily-nav-no-changes-hint">
            No changes to save.
          </Typography>
        )}
        {liveThresholdGroups.map(group => (
          <Box key={group.title} sx={{ mb: 3 }}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary">
              {group.title.toUpperCase()}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {group.fields.map(field => (
                <Grid key={field.key} size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    id={`daily-nav-threshold-${field.key}`}
                    label={field.label}
                    value={thresholds[field.key]}
                    onChange={e => handleThresholdChange(field.key, e.target.value)}
                    error={!isValidThresholdPercent(thresholds[field.key])}
                    disabled={!isAdminOrStaffOrSuperuser}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h4" gutterBottom>
          Trading Market
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          The market whose trading calendar defines the NAV day. Its session close is when market data is read up to,
          and allocations finalized by then are the ones included in that day&apos;s report. Trading holidays are not NAV
          days, and early closes are handled automatically. Each day uses the market that was in effect on that date.
        </Typography>
        {/* The picker reads as choosing where comp prices come from; say it does not. */}
        <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }} color="text.secondary">
          This sets the day&apos;s timing only. It does not affect which public comps are tracked or where their prices
          come from &mdash; those are set per company in Daily Monitoring.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
          {DAILY_NAV_APPLY_STANDING_EXPLANATION}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, sm: 7 }}>
            <TextField
              select
              fullWidth
              size="small"
              id="daily-nav-trading-market"
              label="Trading Market"
              value={tradingMarket}
              onChange={e => setTradingMarket(e.target.value)}
              error={!tradingMarket}
              disabled={!isAdminOrStaffOrSuperuser}>
              {marketOptions.map(option => (
                <MenuItem key={option.value} value={option.value} data-testid={`daily-nav-market-${option.value}`}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h4" gutterBottom>
          Daily Alert Notification Recipients
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          These recipients receive the daily report at the clock time below, in the trading
          market&apos;s timezone ({dailyNavMarketTimezone(tradingMarket)}) — not UTC and not your
          local timezone.
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TimePicker
              ampm={false}
              format="HH:mm"
              views={['hours', 'minutes']}
              label={`Report send time (${dailyNavMarketTimezone(tradingMarket)})`}
              value={parseDailyNavReportSendTime(reportSendTime)}
              onChange={value => {
                if (!value || Number.isNaN(value.getTime())) {
                  return;
                }
                setReportSendTime(snapDailyNavReportSendTime(value));
              }}
              timeSteps={{ minutes: REPORT_SEND_TIME_MINUTE_STEP }}
              minutesStep={REPORT_SEND_TIME_MINUTE_STEP}
              skipDisabled
              shouldDisableTime={(value, view) =>
                view === 'minutes' && value.getMinutes() % REPORT_SEND_TIME_MINUTE_STEP !== 0
              }
              disabled={!isAdminOrStaffOrSuperuser}
              slotProps={{
                textField: {
                  id: 'daily-nav-report-send-time',
                  size: 'small',
                  fullWidth: true,
                  error: !isValidReportSendTime(reportSendTime),
                },
              }}
            />
          </Grid>
        </Grid>
        <Stack spacing={2}>
          {recipients.map((recipient, index) => (
            <Grid container spacing={2} key={recipient.key} alignItems="center">
              <Grid size={{ xs: 10, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  placeholder="e.g. alerts@firm.com"
                  data-testid={`daily-nav-recipient-${index}`}
                  value={recipient.email}
                  onChange={e => handleRecipientChange(index, e.target.value)}
                  error={Boolean(recipient.email) && !EMAIL_PATTERN.test(recipient.email.trim())}
                  disabled={!isAdminOrStaffOrSuperuser}
                />
              </Grid>
              <Grid size={{ xs: 2, sm: 1 }} sx={{ textAlign: 'right' }}>
                <IconButton
                  color="error"
                  data-testid={`daily-nav-recipient-remove-${index}`}
                  onClick={() => handleRemoveRecipient(index)}
                  disabled={!isAdminOrStaffOrSuperuser}>
                  <DeleteOutline />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Stack>
        <Box sx={{ mt: 2 }}>
          <Button
            startIcon={<Add />}
            onClick={handleAddRecipient}
            variant="text"
            id="daily-nav-add-recipient"
            disabled={!isAdminOrStaffOrSuperuser}>
            Add Recipient
          </Button>
        </Box>
        <DailyNavTemplateMapper
          disabled={!isAdminOrStaffOrSuperuser}
          isUploading={isUploadingTemplate}
          templateFile={templateFile}
          reportTemplateId={reportTemplateId}
          reportTemplateFilename={reportTemplateFilename}
          reportColumnMappings={reportColumnMappings}
          reportMappingFields={reportMappingFields}
          onFileChange={handleTemplateFileChange}
          onReportColumnMappingsChange={setReportColumnMappings}
        />
        <DailyNavFtpConfig
          disabled={!isAdminOrStaffOrSuperuser}
          ftpHost={ftpHost}
          ftpPort={ftpPort}
          ftpPath={ftpPath}
          ftpUsername={ftpUsername}
          onFtpHostChange={setFtpHost}
          onFtpPortChange={setFtpPort}
          onFtpPathChange={setFtpPath}
          onFtpUsernameChange={setFtpUsername}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 5 }} sx={{ mt: { xs: 2, lg: '1.25em' } }}>
        <Paper elevation={4} sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Version History
          </Typography>
          {history?.length ? (
            <List dense>
              {history.map(version => (
                /* secondaryAction keeps the Apply button out of ListItemButton so the trailing
                     action isn't nested inside the row's own interactive element. */
                <ListItem
                  key={version.id}
                  disablePadding
                  secondaryAction={
                    version.is_active && isAdminOrStaffOrSuperuser ? (
                    // <span> required: a disabled MUI Button fires no hover events, so
                    // Tooltip needs it to render at all. aria-label carries the same reason,
                    // since a disabled button isn't focusable.
                      <Tooltip title={applyDisabledReason} describeChild>
                        <span
                          data-testid="daily-nav-version-history-apply-tooltip"
                          aria-label={applyDisabledReason || undefined}>
                          <Button
                            size="small"
                            variant="outlined"
                            data-testid="daily-nav-version-history-apply-button"
                            onClick={event => {
                              event.stopPropagation();
                              setApplyDialog({ versionId: version.id ?? null, fromSave: false });
                            }}
                            disabled={isApplyDisabled}>
                            {DAILY_NAV_APPLY_BUTTON_LABEL}
                          </Button>
                        </span>
                      </Tooltip>
                    ) : undefined
                  }>
                  <ListItemButton
                    selected={version.id === selectedVersionId}
                    data-testid={`daily-nav-firm-version-${version.id}`}
                    onClick={() =>
                      setSelectedVersionId(version.id === selectedVersionId ? null : (version.id ?? null))
                    }>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <span>{formatEffectivePeriod(version)}</span>
                          {/* Two facts, not one: after a mid-day save a lone "Current"
                              chip marks the version NOT driving today's numbers.
                              Collapses to one chip when they agree. */}
                          {version.id === anchoredVersionId && (
                            <Chip
                              size="small"
                              color={hasDivergence ? 'warning' : 'success'}
                              label={
                                hasDivergence
                                  ? `In effect for ${dailyNavDateLabel(governingNavDate)} NAV`
                                  : 'Current — today’s NAV and the next'
                              }
                              data-testid={`daily-nav-firm-version-in-effect-${version.id}`}
                            />
                          )}
                          {hasDivergence && version.id === openVersionId && (
                            <Chip
                              size="small"
                              color="info"
                              label={dailyNavNextDayLabel(nextNavDate)}
                              data-testid={`daily-nav-firm-version-next-${version.id}`}
                            />
                          )}
                          {/* No open NAV day to compare against; still mark the open one. */}
                          {anchoredVersionId === null && version.is_active && (
                            <Chip
                              size="small"
                              color="success"
                              label="Current"
                              data-testid={`daily-nav-firm-version-current-${version.id}`}
                            />
                          )}
                        </Stack>
                      }
                      secondary={version.created_by_name ? `Changed by ${version.created_by_name}` : undefined}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No configuration changes have been saved yet.</Typography>
          )}
          {hasMoreHistory && (
            <Button
              fullWidth
              size="small"
              onClick={() => loadMoreHistory()}
              disabled={isLoadingMoreHistory}
              data-testid="daily-nav-firm-history-load-more">
              {isLoadingMoreHistory ? 'Loading...' : 'Load More'}
            </Button>
          )}
          {selectedVersion && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Values in effect {formatEffectivePeriod(selectedVersion)}
              </Typography>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                TRADING SESSION WINDOWS
              </Typography>
              {DAILY_NAV_WINDOW_SESSIONS_FIELDS.map(field => (
                <Typography key={field.key} variant="body2">
                  {field.label}: {selectedVersion[field.key]}
                </Typography>
              ))}
              {getDailyNavThresholdGroups(
                selectedVersion.short_window_sessions,
                selectedVersion.long_window_sessions
              ).map(group => (
                <Box key={group.title} sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    {group.title.toUpperCase()}
                  </Typography>
                  {group.fields.map(field => (
                    <Typography key={field.key} variant="body2">
                      {field.label}: {fractionToPercent(selectedVersion[field.key]) || '—'}%
                    </Typography>
                  ))}
                </Box>
              ))}
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                TRADING MARKET
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {tradingMarketLabel(selectedVersion.trading_calendar)}
              </Typography>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                REPORT SEND TIME
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {formatDailyNavReportSendTime(selectedVersion.report_send_time)}{' '}
                {dailyNavMarketTimezone(selectedVersion.trading_calendar)}
              </Typography>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                NOTIFICATION RECIPIENTS
              </Typography>
              <Typography variant="body2">
                {Array.isArray(selectedVersion.notification_recipients) &&
                selectedVersion.notification_recipients.length
                  ? (selectedVersion.notification_recipients as string[]).join(', ')
                  : 'None'}
              </Typography>
              <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" sx={{ mt: 1 }}>
                REPORT TEMPLATE
              </Typography>
              <Typography variant="body2">
                {selectedVersion.report_template_filename || 'None'}
              </Typography>
              {(selectedVersion.report_column_map ?? []).map(
                (mapping: DailyNavReportColumnMapping) => (
                  <Typography key={mapping.column_index} variant="body2" color="text.secondary">
                    {mapping.column_name || '(blank header)'} →{' '}
                    {mapping.kind === DailyNavReportColumnMapping.kind.LITERAL
                      ? `"${mapping.value ?? ''}"`
                      : selectedVersion.report_mapping_fields?.find(field => field.key === mapping.field)?.label ??
                        mapping.field}
                  </Typography>
                )
              )}
            </Box>
          )}
        </Paper>
      </Grid>
      <Dialog
        open={applyDialog !== null}
        onClose={() => setApplyDialog(null)}
        data-testid="daily-nav-apply-confirm-dialog">
        <DialogTitle>{applyDialogTitle}</DialogTitle>
        <DialogContent>
          {/* Both forms: what changed is the reason to open this at all. */}
          <DailyNavConfigDiff
            rows={dialogDiff}
            fromLabel={
              canApplyNow ? `In effect for ${dailyNavDateLabel(governingNavDate)} NAV` : 'Previous'
            }
            toLabel={canApplyNow ? 'After applying' : 'Saved'}
            testId="daily-nav-apply-diff"
          />
          <Typography variant="body2" sx={{ mt: dialogDiff.length ? 2 : 0 }}>
            {dailyNavNextDayLabel(nextNavDate)}.
          </Typography>
          {canApplyNow ? (
            <>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Applying now also re-points {recordCount} record{recordCount === 1 ? '' : 's'} for{' '}
                {dailyNavApplyDatesLabel(applicableDates)}, the day still in progress. This cannot be undone.
              </Typography>
              {marketOrWindowsChange && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  {DAILY_NAV_APPLY_MARKET_OR_WINDOWS_CHANGE_WARNING}
                </Typography>
              )}
            </>
          ) : (
            // Why no apply is offered, rather than leaving it unexplained.
            applyUnavailableReason && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1 }}
                data-testid="daily-nav-apply-unavailable-reason">
                {applyUnavailableReason}
              </Typography>
            )
          )}
        </DialogContent>
        <DialogActions>
          {canApplyNow ? (
            <>
              {/* Not "Cancel": the settings are saved either way, so dismissal must not
                  read as "my save was discarded". */}
              <Button onClick={() => setApplyDialog(null)} data-testid="daily-nav-apply-next-day-button">
                Next NAV day only
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmApplyToOpenNavDay}
                // The handler bails out silently on a null id: disable, do not no-op.
                disabled={isApplyPending || applyVersionId === null}
                data-testid="daily-nav-apply-confirm-button">
                Apply now
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setApplyDialog(null)} data-testid="daily-nav-apply-done-button">
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default DailyNavSettings;
